import { logVueErrorHandler } from "@/log/log.ts";
import router from "@/router/index";
import { useGlobalStore } from "@/store/globals";
import { PiniaColada } from "@pinia/colada";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import Notifications from "@kyvg/vue3-notification";
import * as Sentry from "@sentry/vue";
import { createHead } from "@vueuse/head";
import { createPinia, type PiniaPluginContext } from "pinia";
import { createApp, markRaw } from "vue";
import type { Router } from "vue-router";
import App from "./App.vue";
import VueVirtualScroller from "vue-virtual-scroller";
import { ENV } from "varlock/env";
import "@/assets/roboto.css";
import "@/assets/aventa.css";
import "@/assets/index.css";
import "@/assets/notify.css";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import { MINUTES, SECONDS } from "@curasoft/frontend.core/other";

function piniaRouterPlugin({ store }: PiniaPluginContext) {
  store.router = markRaw(router);
}

async function initialize(): Promise<void> {
  await import("./FontAwesomeLibrary");
  const app = createApp(App);
  app.component("IconFA", FontAwesomeIcon);
  app.use(Notifications);

  const pinia = createPinia();
  pinia.use(piniaRouterPlugin);

  app.use(router);
  app.use(pinia);
  // noinspection PointlessArithmeticExpressionJS
  app.use(PiniaColada, {
    queryOptions: {
      gcTime: 5 * MINUTES,
      // Reuse just-loaded data across consumers. These triggers refresh stale
      // entries; staleTime/gcTime do not schedule periodic requests. Large
      // reports can define their own coalescing window in src/queries.
      staleTime: 30 * SECONDS,

      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
  });
  app.use(VueVirtualScroller);
  app.use(createHead());

  const globalStore = useGlobalStore();

  // eslint-disable-next-line no-console
  console.log(
    import.meta.env.PROD ? "sentry enabled" : "sentry off",
    ENV.VERSION,
  );
  Sentry.init({
    app,
    enabled: import.meta.env.PROD,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: `csi@${ENV.VERSION}`,
    integrations: [
      Sentry.contextLinesIntegration(),
      Sentry.browserTracingIntegration({ router }),
      Sentry.vueIntegration({
        tracingOptions: {
          trackComponents: true,
        },
      }),
      Sentry.browserProfilingIntegration(),
    ],
    tracesSampleRate: 0.1,
    tracePropagationTargets: [],
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,
    profilesSampleRate: globalStore.sentrySampleRate,
  });
  app.config.errorHandler = logVueErrorHandler;
  app.mount("#app");
}

declare module "pinia" {
  interface PiniaCustomProperties {
    router: Router;
  }
}

void initialize();
